// What counts as a number as the memo writes one, and where in a text such numbers sit.
//
// A figure ref marks a character span of a block's text as one of the ledger's numbers. The page

import { CURRENCY_CODES } from "./figure";

export const NUMBER_AS_WRITTEN = /^[$€£¥]?[+\-−]?\d(?:[\d,\u00a0\u202f]*\d)?(?:\.\d+)?%?$/;

const FENCE_LINE = /^(```|~~~)/;
const INDENTED_CODE = /^(?: {4}|\t)/;

// True when `at` sits inside a code block: an odd number of fence lines above it, or a line that
// starts with four spaces or a tab, or an open backtick. HTML injected there is shown as text.
export const insideCode = (text: string, at: number): boolean => {
  const before = text.slice(0, at);
  const lines = before.split("\n");
  const fences = lines.filter((line) => FENCE_LINE.test(line)).length;
  if (fences % 2 === 1) return true;
  if (INDENTED_CODE.test(lines[lines.length - 1])) return true;
  return (before.match(/`/g) ?? []).length % 2 === 1;
};

const continuesNumber = (next: string | undefined, beyond: string | undefined): boolean =>
  /\d/.test(next ?? "") || (/[,.\u00a0\u202f]/.test(next ?? "") && /\d/.test(beyond ?? ""));

export const spanCoversFigure = (text: string, start: number, len: number): boolean => {
  if (!Number.isInteger(start) || !Number.isInteger(len) || start < 0 || len <= 0) return false;
  const end = start + len;
  if (end > text.length) return false;
  if (!/\d/.test(text.slice(start, end))) return false;
  if (continuesNumber(text[start - 1], text[start - 2]) || continuesNumber(text[end], text[end + 1])) return false;
  return !insideCode(text, start);
};

// The value a number as written stands for, or null when the text is not one.
export const numberAsWritten = (covered: string): number | null => {
  if (!NUMBER_AS_WRITTEN.test(covered)) return null;
  const written = Number(
    covered
      .replace(/^[$€£¥]/, "")
      .replace(/^−/, "-")
      .replace(/[,\u00a0\u202f]/g, "")
      .replace(/%$/, "")
  );
  return Number.isFinite(written) ? written : null;
};

export type TNumberSpan = { start: number; end: number; text: string };

// A number the way a ref covers one: digits with thousands separators (a comma or a non-breaking
// space, never a plain space, which would fuse "2027 3 targets") and decimals, an explicit sign, a
// trailing percent; never the currency sign before it, the unit after it, or a trailing separator.
const NUMBER_TOKEN = /(?<![\d.,])[+\-−]?\d(?:[\d,\u00a0\u202f]*\d)?(?:\.\d+)?%?/g;
// A number joined to another by a colon, a slash, a dash or a dot is a part of a date, a time, a
// ratio or a version, not a figure; so is one followed by a CJK date or time unit (2026年9月15日).
const JOINER = /[-:/.]/;
const CJK_UNIT = /[年月日時分秒]/;

const partOfCompound = (text: string, start: number, end: number): boolean => {
  const before = text[start - 1] ?? "";
  const after = text[end] ?? "";
  if (JOINER.test(before) && /\d/.test(text[start - 2] ?? "")) return true;
  if (JOINER.test(after) && /\d/.test(text[end + 1] ?? "")) return true;
  return CJK_UNIT.test(after);
};

export const findNumberSpans = (text: string): TNumberSpan[] => {
  const spans: TNumberSpan[] = [];
  for (const match of text.matchAll(NUMBER_TOKEN)) {
    const start = match.index;
    const end = start + match[0].length;
    if (partOfCompound(text, start, end)) continue;
    if (insideCode(text, start)) continue;
    spans.push({ start, end, text: match[0] });
  }
  return spans;
};

const CELL_NUMBER = new RegExp(
  `^([+\\-−])?(?:[A-Z]{1,3}\\$|[$€£¥]|(?:${CURRENCY_CODES.join("|")})[ \\u00a0])?` +
    `([+\\-−]?\\d(?:[\\d,\\u00a0\\u202f]*\\d)?(?:\\.\\d+)?%?)(\\s*[\\p{L}\\p{Nl}\\p{No}×][\\p{L}\\p{Nl}\\p{No}×\\s.]*)?$`,
  "u"
);

const APPROXIMATELY = /^[~≈]\s*/;
const TRAILING_NOTE = /(\S)\s*\([^()]*\)$/;
const ACCOUNTING_NEGATIVE = /^\((.+)\)$/;

export const numberInCell = (text: string): string | null => {
  const trimmed = text.trim().replace(TRAILING_NOTE, "$1").replace(APPROXIMATELY, "");
  const negative = ACCOUNTING_NEGATIVE.exec(trimmed);
  if (negative) {
    const inside = numberInCell(negative[1]);
    return inside !== null && !/^[+\-−]/.test(inside) ? `-${inside}` : null;
  }
  if (numberAsWritten(trimmed) !== null) return trimmed;
  const match = CELL_NUMBER.exec(trimmed);
  if (!match || CJK_UNIT.test(match[3]?.trim()[0] ?? "")) return null;
  if (match[1] && /^[+\-−]/.test(match[2])) return null;
  return `${match[1] ?? ""}${match[2]}`;
};
export * from "./figure";
export * from "./fill";
export * from "./shaped";
